import React, { Component, ErrorInfo, ReactNode } from "react";
import Button from "../button/Button";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** フォールバックUIをカスタマイズする場合に指定 */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * エラーバウンダリコンポーネント
 *
 * 子コンポーネントのレンダリング中にエラーが発生した場合、
 * 画面全体が白くなる代わりにフォールバックUIを表示する。
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("[ErrorBoundary] Uncaught error:", error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "50vh",
            padding: "24px",
            gap: "16px",
          }}
        >
          <p className="heading-16">エラーが発生しました</p>
          <p className="bodytext-13 u-text-light-gray">
            予期しないエラーが発生しました。ページを再読み込みしてください。
          </p>
          <div style={{ display: "flex", gap: "12px" }}>
            <Button
              size="small"
              theme="tertiary"
              variant="filled"
              onClick={this.handleReset}
            >
              再試行
            </Button>
            <Button
              size="small"
              theme="primary"
              variant="filled"
              onClick={this.handleReload}
            >
              ページを再読み込み
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
